import type { DealDetailPayload } from '../../types';

/**
 * Whether the deal card's action bar (Log Deal / Push · Log Pass · Dismiss · Merge)
 * is shown. Enabled when there is anything to act on:
 *  - `is_pending` — unreviewed AI proposals (pending_count > 0);
 *  - `unpushed_field_count > 0` — reviewed (accepted/edited) fields not yet pushed;
 *  - `has_unpushed_note_edit` — an operator note edit not yet pushed;
 *  - **DU-1** — an OPEN candidate, which ALWAYS awaits Log Deal / Dismiss / Merge,
 *    even at pending_count 0 (its only proposal may be the synthetic deal-team row,
 *    which `_pending_count` deliberately ignores). Without this the card goes empty
 *    and mislabels itself "in sync with Affinity" — a candidate was never in Affinity.
 *
 * `payload.status` for a candidate is the lifecycle status (`candidate_deals.status`:
 * open|promoted|dismissed|merged_into); for an affinity_opp it is hardcoded 'pending'.
 * So the candidate clause is live, not dead (the reason it reads on deal_kind, not a
 * bare `status === 'open'`).
 *
 * Pure + unit-tested (`DealsPage.actionState.test.ts`); pre-extracted for the D4
 * DealsPage componentization.
 */
export function dealActionsEnabled(
  p: Pick<DealDetailPayload,
    'is_pending' | 'unpushed_field_count' | 'has_unpushed_note_edit' | 'deal_kind' | 'status'>,
): boolean {
  return (
    p.is_pending ||
    p.unpushed_field_count > 0 ||
    p.has_unpushed_note_edit ||
    (p.deal_kind === 'candidate' && p.status === 'open')
  );
}
